import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Card, Button, Table, Tag, Space, message, Modal, Form, Input, Upload, Select, Tabs } from 'antd';
import api from '../api/http';
import dayjs from 'dayjs';
import SimplePdfViewer from '../components/SimplePdfViewer';
import { openWithAuth } from '../utils/authedResource';

const onboardingSteps = [
  { id: 'apply', index: 1, title: '入职申请', desc: '候选人发起入职申请或由招聘系统生成。', color: '#2563eb' },
  { id: 'training', index: 2, title: '培训教育', desc: '项目部开展岗前培训，确认合格后进入下一步。', color: '#15803d' },
  { id: 'medical', index: 3, title: '入职体检', desc: '候选人完成入职体检并出具合格证明。', color: '#d97706' },
  { id: 'dorm', index: 4, title: '入住宿舍', desc: '完成宿舍分配并记录床位信息。', color: '#b45309' },
  { id: 'follow', index: 5, title: '实习跟踪', desc: '对实习期员工进行阶段性跟踪和评估。', color: '#b91c1c' }
];

const offboardingSteps = [
  { id: 'submit_resign', index: 1, title: '提交辞职', desc: '提交离职/解聘申请，进入流程。', color: '#2563eb' },
  { id: 'sign_review', index: 2, title: '审核签字', desc: '完成审批签字，等待生效离岗。', color: '#15803d' },
  { id: 'leave_due', index: 3, title: '到期离岗', desc: '到生效日后执行离岗并归档。', color: '#d97706' },
  { id: 'insurance_exit', index: 4, title: '保险退出', desc: '办理社保等保险退出。', color: '#b45309' },
  { id: 'exit_medical', index: 5, title: '离职体检', desc: '完成离职体检资料留存。', color: '#b91c1c' }
];

const containerStyle = { display: 'flex', flexDirection: 'column', gap: 18 };
const stepListStyle = { display: 'flex', gap: 12, flexWrap: 'wrap' };
const stepCardStyle = (bg, active) => ({
  flex: '1 1 180px', minWidth: 160, background: bg, borderRadius: 12, padding: 14, cursor: 'pointer', boxShadow: active ? '0 12px 28px rgba(15,23,42,0.12)' : '0 8px 20px rgba(15,23,42,0.06)', border: active ? '2px solid rgba(0,0,0,0.06)' : '1px solid rgba(0,0,0,0.04)'
});

const EmployeeOnboardingFlowPage = () => {
  const [activeFlowTab, setActiveFlowTab] = useState('onboarding');
  const [activeStep, setActiveStep] = useState('apply');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  // 培训数据（台账 + 委托）
  const [ledgers, setLedgers] = useState([]);
  const [entrusts, setEntrusts] = useState([]);
  const [trainingLoading, setTrainingLoading] = useState(false);
  // 弹窗与银行卡本地保存
  const [viewModal, setViewModal] = useState({ open: false, record: null, bankCard: '' });
  const [bankInfoMap, setBankInfoMap] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem('employeeApplicationBankInfo') || '{}'); } catch (e) { return {}; }
  });

  // 上传与资料查看（入职体检）
  const [uploadModal, setUploadModal] = useState({ open: false, record: null });
  const [uploadFiles, setUploadFiles] = useState([]);
  const [docName, setDocName] = useState('');
  const [docTypes, setDocTypes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docViewer, setDocViewer] = useState({ open: false, record: null, list: [], loading: false });
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: '', fileName: '' });
  const [deletingDocId, setDeletingDocId] = useState(null);

  // 入住宿舍选择
  const [dormModal, setDormModal] = useState({ open: false, record: null, dorms: [], selectedDorm: null, selectedRoom: null, selectedBed: null, loading: false });

  const steps = activeFlowTab === 'offboarding' ? offboardingSteps : onboardingSteps;

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const params = activeFlowTab === 'onboarding'
        ? { change_type: 'hire', page_size: 200 }
        : { page_size: 200 };
      const res = await api.get('/employee_changes/applications', { params });
      const records = (res.data && res.data.success)
        ? (Array.isArray(res.data.records) ? res.data.records : [])
        : (Array.isArray(res.data) ? res.data : []);
      if (activeFlowTab === 'offboarding') {
        setApplications(records.filter(r => ['resign', 'termination'].includes(String(r.change_type || '').toLowerCase())));
      } else {
        setApplications(records);
      }
    } catch (err) {
      console.error('fetch onboarding applications error', err);
      message.error('获取申请数据失败');
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }, [activeFlowTab]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  useEffect(() => {
    setActiveStep(activeFlowTab === 'offboarding' ? 'submit_resign' : 'apply');
  }, [activeFlowTab]);

  const loadEntrusts = useCallback(() => {
    try {
      const raw = window.localStorage.getItem('trainingEntrustRecords');
      const parsed = raw ? JSON.parse(raw) : [];
      const rows = [];
      (Array.isArray(parsed) ? parsed : []).forEach(rec => {
        if (rec && Array.isArray(rec.rows)) {
          rec.rows.forEach(r => {
            if (r && r.name && String(r.name).trim()) rows.push({ name: String(r.name).trim(), entrustDate: rec.entrustDate || null, entrustId: rec.id });
          });
        }
      });
      setEntrusts(rows);
    } catch (e) {
      console.warn('解析 trainingEntrustRecords 失败', e);
      setEntrusts([]);
    }
  }, []);

  const fetchLedgers = useCallback(async () => {
    setTrainingLoading(true);
    try {
      const res = await api.get('/education-training/ledgers');
      if (res && res.data) {
        setLedgers(Array.isArray(res.data) ? res.data : (Array.isArray(res) ? res : []));
      } else {
        setLedgers([]);
      }
    } catch (e) {
      console.warn('获取培训台账失败', e);
      setLedgers([]);
    } finally {
      setTrainingLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEntrusts();
  }, [loadEntrusts]);

  useEffect(() => {
    if (activeStep === 'training') {
      fetchLedgers();
      loadEntrusts();
    }
  }, [activeStep, fetchLedgers, loadEntrusts]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onStorage = (ev) => {
      if (ev?.key === 'trainingEntrustRecords') loadEntrusts();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [loadEntrusts]);

  const normalizeName = (n) => (n ? String(n).trim().toLowerCase() : '');

  const trainingList = useMemo(() => {
    const map = new Map();
    (ledgers || []).forEach(l => {
      const name = normalizeName(l.employee_name_official || l.employee_name || '');
      if (!name) return;
      const stages = [
        { key: 'project', label: '项目部教育', date: l.project_training_time, score: l.project_score },
        { key: 'company', label: '公司级教育', date: l.company_training_time, score: l.company_score },
        { key: 'site', label: '厂级教育', date: l.site_training_time, score: l.site_score },
        { key: 'team', label: '班组级教育', date: l.team_training_time, score: l.team_score }
      ];
      let latest = null;
      stages.forEach(s => {
        if (s.date) {
          if (!latest) latest = s;
          else if (new Date(s.date) > new Date(latest.date)) latest = s;
        }
      });
      map.set(name, {
        name: l.employee_name_official || l.employee_name,
        source: '台账',
        stage: latest ? latest.label : '未开始',
        stageDate: latest ? latest.date : null,
        score: latest ? latest.score : null,
        rawLedger: l
      });
    });
    (entrusts || []).forEach(e => {
      const name = normalizeName(e.name);
      if (!name) return;
      if (!map.has(name)) {
        map.set(name, { name: e.name, source: '委托', stage: '未建台账', stageDate: e.entrustDate || null, score: null, entrustId: e.entrustId });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [ledgers, entrusts]);

  const filtered = useMemo(() => {
    if (!Array.isArray(applications)) return [];
    if (activeFlowTab === 'offboarding') {
      if (activeStep === 'submit_resign') return applications.filter(a => a.status === 'PendingApproval');
      if (activeStep === 'sign_review') return applications.filter(a => ['PendingResign', 'Approved'].includes(a.status));
      if (activeStep === 'leave_due') return applications.filter(a => ['PendingResign', 'Approved'].includes(a.status));
      if (activeStep === 'insurance_exit') return applications.filter(a => a.status === 'Resigned');
      if (activeStep === 'exit_medical') return applications.filter(a => a.status === 'InsuranceExited');
      return applications;
    }
    if (activeStep === 'apply') return applications;
    if (activeStep === 'training') return applications.filter(a => a.status === 'Approved');
    if (activeStep === 'medical') return applications.filter(a => a.status === 'Hired');
    if (activeStep === 'dorm') return applications.filter(a => a.status === 'Hired');
    if (activeStep === 'follow') return applications.filter(a => a.status === 'Hired');
    return applications;
  }, [applications, activeStep, activeFlowTab]);

  const openViewModal = (rec) => {
    setViewModal({ open: true, record: rec, bankCard: bankInfoMap[rec.id] || '' });
  };
  const closeViewModal = () => setViewModal({ open: false, record: null, bankCard: '' });

  const saveBankInfo = () => {
    const rec = viewModal.record;
    if (!rec) return;
    const bank = String(viewModal.bankCard || '').trim();
    const next = { ...bankInfoMap, [rec.id]: bank };
    try {
      window.localStorage.setItem('employeeApplicationBankInfo', JSON.stringify(next));
      setBankInfoMap(next);
      message.success('银行卡信息已保存');
      // 保持模态窗口中显示最新
      setViewModal(v => ({ ...v, bankCard: bank }));
    } catch (e) {
      console.error('保存银行卡信息失败', e);
      message.error('保存失败');
    }
  };

  const markForTraining = (rec) => {
    try {
      const storageKey = 'trainingEntrustRecords';
      const raw = window.localStorage.getItem(storageKey);
      const arr = raw ? JSON.parse(raw) : [];
      const today = new Date().toISOString().slice(0, 10);
      const candidateName = rec.candidate_name || rec.candidate_name_official || '';
      const exists = arr.some(r => Array.isArray(r.rows) && r.entrustDate === today && r.rows.some(row => row.name === candidateName));
      if (exists) { message.info('候选人已在今日的培训列表中'); return; }
      const newRecord = {
        id: (crypto?.randomUUID ? crypto.randomUUID() : `record-${Date.now()}`),
        entrustUnit: '上杭县众邦劳务有限公司',
        entrustDate: today,
        rows: [{ name: candidateName, origin: rec.target_department || '', idNumber: rec.candidate_id_number || '', jobType: rec.target_position || '', certificateNumber: '', remark: '' }]
      };
      const next = [newRecord, ...arr];
      window.localStorage.setItem(storageKey, JSON.stringify(next));
      try { window.dispatchEvent(new StorageEvent('storage', { key: storageKey, newValue: JSON.stringify(next) })); } catch (e) { /* ignore */ }
      message.success('已添加到培训列表');
    } catch (e) {
      console.error('添加到培训列表失败', e);
      message.error('添加失败');
    }
  };

  // 文档查看/上传（入职体检）
  const openDocViewer = async (record) => {
    setDocViewer({ open:true, list:[], record, loading:true });
    try {
      const res = await api.get(`/employee_changes/applications/${record.id}/documents`);
      if (res.data && res.data.success) setDocViewer({ open:true, list: res.data.records || [], record, loading:false });
      else { message.error(res.data?.error || '获取资料失败'); setDocViewer(v=>({ ...v, loading:false })); }
    } catch (e) { console.error('获取资料失败', e); message.error('获取资料失败'); setDocViewer(v=>({ ...v, loading:false })); }
  };
  const closeDocViewer = () => setDocViewer({ open:false, list:[], record:null, loading:false });

  const deleteDocument = async (doc) => {
    const appId = docViewer.record?.id;
    if (!appId || !doc?.id) return;
    Modal.confirm({
      title: '确认删除资料？',
      content: '删除后无法恢复。',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        setDeletingDocId(doc.id);
        try {
          const res = await api.delete(`/employee_changes/applications/${appId}/documents/${doc.id}`);
          if (res.data && res.data.success) {
            message.success('资料已删除');
            await openDocViewer(docViewer.record);
          } else {
            message.error(res.data?.error || '删除失败');
          }
        } catch (e) {
          console.error('删除资料失败', e);
          message.error(e?.response?.data?.error || '删除资料失败');
        } finally {
          setDeletingDocId(null);
        }
      }
    });
  };

  const openUpload = (record) => {
    const isOffboardingMedical = activeFlowTab === 'offboarding' && activeStep === 'exit_medical';
    setUploadModal({ open: true, record });
    setUploadFiles([]);
    setDocName('');
    setDocTypes(isOffboardingMedical ? ['exit_medical'] : ['health_check']);
  };
  const closeUpload = () => setUploadModal({ open: false, record: null });

  const uploadProps = {
    accept: '.pdf',
    maxCount: 1,
    beforeUpload: (file) => {
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      if (!isPdf) { message.error('只支持上传 PDF'); return Upload.LIST_IGNORE; }
      setUploadFiles([{ uid: file.uid || file.name, name: file.name, originFileObj: file }]);
      return false;
    },
    onRemove: () => setUploadFiles([]),
    fileList: uploadFiles
  };

  const submitUpload = async () => {
    if (!uploadModal.record) return;
    if (!uploadFiles.length) { message.error('请先选择 PDF 文件'); return; }
    if (!docName) { message.error('请填写资料名称'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFiles[0].originFileObj);
      formData.append('name', docName);
      formData.append('types', JSON.stringify((docTypes && docTypes.length) ? docTypes : ['health_check']));
      const id = uploadModal.record.id;
      const res = await api.post(`/employee_changes/applications/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data && res.data.success) {
        message.success('资料上传成功');
        closeUpload();
        if (docViewer.record?.id === id) openDocViewer(docViewer.record);
        fetchApplications();
      } else message.error(res.data?.error || '上传失败');
    } catch (e) {
      console.error('上传异常', e);
      message.error(e?.response?.data?.error || e.message || '上传异常');
    } finally { setUploading(false); }
  };

  const executeResign = async (rec) => {
    try {
      await api.post(`/employee_changes/applications/${rec.id}/execute_resign`, {});
      message.success('已执行到期离岗');
      fetchApplications();
    } catch (e) {
      console.error('执行离岗失败', e);
      message.error(e?.response?.data?.error || '执行离岗失败');
    }
  };

  const completeInsuranceExit = async (rec) => {
    try {
      await api.post(`/employee_changes/applications/${rec.id}/complete_insurance_exit`, {});
      message.success('保险退出已完成');
      fetchApplications();
    } catch (e) {
      if (e?.response?.status === 404) {
        try {
          await api.post(`/employee_changes/applications/${rec.id}/insurance_exit`, {});
          message.success('保险退出已完成');
          fetchApplications();
          return;
        } catch (fallbackError) {
          console.error('完成保险退出失败(回退接口)', fallbackError);
          message.error(fallbackError?.response?.data?.error || '完成保险退出失败');
          return;
        }
      }
      console.error('完成保险退出失败', e);
      message.error(e?.response?.data?.error || '完成保险退出失败');
    }
  };

  const completeExitMedical = async (rec) => {
    try {
      await api.post(`/employee_changes/applications/${rec.id}/complete_exit_medical`, {});
      message.success('离职体检已完成');
      fetchApplications();
    } catch (e) {
      console.error('完成离职体检失败', e);
      message.error(e?.response?.data?.error || '完成离职体检失败');
    }
  };

  // 入住宿舍
  const openDormModal = async (record) => {
    setDormModal({ open:true, record, dorms: [], selectedDorm: null, selectedRoom: null, selectedBed: null, loading: true });
    try {
      const res = await api.get('/dorms');
      if (res.data && res.data.dorms) setDormModal(d => ({ ...d, dorms: res.data.dorms, loading: false }));
      else setDormModal(d => ({ ...d, dorms: [], loading: false }));
    } catch (e) { console.error('加载宿舍失败', e); message.error('加载宿舍失败'); setDormModal(d => ({ ...d, dorms: [], loading: false })); }
  };
  const closeDormModal = () => setDormModal({ open:false, record:null, dorms: [], selectedDorm: null, selectedRoom: null, selectedBed: null, loading: false });
  const submitDorm = async () => {
    const rec = dormModal.record; if (!rec) return;
    const roomId = dormModal.selectedRoom; const bedIndex = dormModal.selectedBed; const name = rec.candidate_name || rec.applicant_name || '';
    if (!roomId || !bedIndex) { message.error('请选择房间与床位'); return; }
    try {
      await api.post(`/dorms/rooms/${roomId}/occupants/checkin`, { bed_index: bedIndex, occupant_name: name });
      message.success('宿舍分配成功');
      closeDormModal();
      fetchApplications();
    } catch (e) {
      console.error('宿舍分配失败', e); message.error(e?.response?.data?.error || '宿舍分配失败');
    }
  };

  const columns = [
    { title: '候选人', dataIndex: 'candidate_name', key: 'candidate_name', render: (t, r) => t || r.applicant_name || '—' },
    { title: '目标部门', dataIndex: 'target_department', key: 'target_department' },
    { title: '申请日期', dataIndex: 'created_at', key: 'created_at', render: (t) => t ? dayjs(t).format('YYYY-MM-DD') : '—' },
    { title: '状态', dataIndex: 'status', key: 'status', render: (s) => {
      const map = {
        PendingApproval: { color: 'gold', text: '待审批' },
        Approved: { color: 'green', text: '已审批' },
        PendingResign: { color: 'processing', text: '待离岗' },
        Resigned: { color: 'blue', text: '已离岗' },
        InsuranceExited: { color: 'purple', text: '保险已退出' },
        ExitMedicalDone: { color: 'cyan', text: '离职体检完成' },
        Hired: { color: 'green', text: '已入职' }
      };
      const conf = map[s] || { color: 'default', text: s || '--' };
      return <Tag color={conf.color}>{conf.text}</Tag>;
    } },
    { title: '操作', key: 'action', render: (_, rec) => (
      <Space>
        <Button size='small' onClick={() => openViewModal(rec)}>查看</Button>
        {(activeFlowTab === 'onboarding' && activeStep === 'medical') && <Button size='small' onClick={() => openDocViewer(rec)}>查看资料</Button>}
        {(activeFlowTab === 'onboarding' && activeStep === 'medical') && <Button size='small' onClick={() => openUpload(rec)}>上传体检</Button>}
        {(activeFlowTab === 'onboarding' && activeStep === 'dorm') && <Button size='small' onClick={() => openDormModal(rec)}>分配宿舍</Button>}
        {(activeFlowTab === 'offboarding' && activeStep === 'leave_due' && ['PendingResign', 'Approved'].includes(rec.status)) && (
          <Button size='small' type='primary' onClick={() => executeResign(rec)}>执行离岗</Button>
        )}
        {(activeFlowTab === 'offboarding' && activeStep === 'insurance_exit' && rec.status === 'Resigned') && (
          <Button size='small' type='primary' onClick={() => completeInsuranceExit(rec)}>完成保险退出</Button>
        )}
        {(activeFlowTab === 'offboarding' && activeStep === 'exit_medical') && <Button size='small' onClick={() => openDocViewer(rec)}>查看资料</Button>}
        {(activeFlowTab === 'offboarding' && activeStep === 'exit_medical') && <Button size='small' onClick={() => openUpload(rec)}>上传体检</Button>}
        {(activeFlowTab === 'offboarding' && activeStep === 'exit_medical' && rec.status === 'InsuranceExited') && (
          <Button size='small' type='primary' onClick={() => completeExitMedical(rec)}>完成离职体检</Button>
        )}
        {(activeFlowTab === 'onboarding') && bankInfoMap && bankInfoMap[rec.id] && String(bankInfoMap[rec.id]).trim() !== '' && (
          <Button size='small' onClick={() => markForTraining(rec)}>培训</Button>
        )}
      </Space>
    ) }
  ];

  return (
    <div style={containerStyle}>
      <Card size='small' style={{ borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>流程看板</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>点击流程步骤查看对应申请列表。</div>
        </div>
        <div>
          <Button onClick={() => fetchApplications()}>刷新</Button>
        </div>
      </Card>

      <Card size='small' style={{ borderRadius: 12 }}>
        <Tabs
          activeKey={activeFlowTab}
          onChange={setActiveFlowTab}
          items={[
            { key: 'onboarding', label: '入职流程' },
            { key: 'offboarding', label: '离职流程' }
          ]}
        />
      </Card>

      <Card size='small' style={{ borderRadius: 12 }}>
        <div style={stepListStyle}>
          {steps.map(s => (
            <div key={s.id} role='button' tabIndex={0} onClick={() => setActiveStep(s.id)} style={stepCardStyle('#fff', activeStep === s.id)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: s.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{s.index}</div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{s.title}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{s.desc}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card size='small' style={{ borderRadius: 12 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{steps.find(st => st.id === activeStep)?.title || '列表'}</div>
          {activeFlowTab === 'onboarding' && activeStep === 'training' && <Button size='small' onClick={() => { fetchLedgers(); loadEntrusts(); }}>刷新培训数据</Button>}
        </div>
        {activeFlowTab === 'onboarding' && activeStep === 'training' ? (
          <Table
            dataSource={trainingList}
            loading={trainingLoading}
            pagination={{ pageSize: 20 }}
            rowKey={(r) => r.rawLedger?.id || r.entrustId || r.name}
            columns={[
              { title: '姓名', dataIndex: 'name', key: 'name' },
              { title: '当前阶段', dataIndex: 'stage', key: 'stage' },
              { title: '阶段日期', dataIndex: 'stageDate', key: 'stageDate', render: (d) => d ? dayjs(d).format('YYYY-MM-DD') : '--' },
              { title: '成绩', dataIndex: 'score', key: 'score', render: (s) => s != null ? s : '--' },
              { title: '来源', dataIndex: 'source', key: 'source' },
              { title: '操作', key: 'action', render: (_, rec) => (
                <Space>
                  {rec.rawLedger && <Button size='small' href={'/education-training/ledger'}>查看台账</Button>}
                  {rec.source === '委托' && <Button size='small' onClick={() => message.info(`委托日期：${rec.stageDate || '—'}`)}>查看委托</Button>}
                </Space>
              ) }
            ]}
          />
        ) : (
          <Table dataSource={filtered} columns={columns} rowKey={(r) => r.id || r.applicant_id || Math.random()} loading={loading} pagination={{ pageSize: 20 }} />
        )}
      </Card>
        <Modal open={viewModal.open} title={`候选人信息 #${viewModal.record?.id || ''}`} onCancel={closeViewModal} footer={null} destroyOnClose>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div><strong>姓名：</strong> {viewModal.record?.candidate_name || viewModal.record?.applicant_name || '--'}</div>
            <div><strong>类型：</strong> {viewModal.record?.change_type || '--'}</div>
            <div><strong>目标部门/岗位：</strong> {viewModal.record?.target_department || '--'} / {viewModal.record?.target_position || '--'}</div>
            <div><strong>生效日期：</strong> {viewModal.record?.effective_date ? dayjs(viewModal.record.effective_date).format('YYYY-MM-DD') : '--'}</div>
            <Form layout='vertical'>
              <Form.Item label='银行卡号' style={{ marginBottom: 0 }}>
                <Input value={viewModal.bankCard} onChange={(e) => setViewModal(v => ({ ...v, bankCard: e.target.value }))} placeholder='请输入银行卡号' />
              </Form.Item>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                <Button onClick={closeViewModal}>取消</Button>
                <Button onClick={saveBankInfo}>保存</Button>
                <Button type='primary' onClick={() => { saveBankInfo(); if (viewModal.record) markForTraining(viewModal.record); }}>保存并培训</Button>
              </div>
            </Form>
          </div>
        </Modal>
        {/* 上传资料弹窗（入职体检）*/}
        <Modal open={uploadModal.open} title={`上传资料 #${uploadModal.record?.id || ''}`} onCancel={closeUpload} footer={null} destroyOnClose>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <Input placeholder='资料名称' value={docName} onChange={(e)=>setDocName(e.target.value)} />
            <Select
              mode='tags'
              placeholder='资料类型(可选)'
              value={docTypes}
              onChange={(v)=>setDocTypes(v)}
              options={activeFlowTab === 'offboarding' && activeStep === 'exit_medical'
                ? [{ value:'exit_medical', label:'离职体检报告' }]
                : [{ value:'health_check', label:'入职体检报告' }]
              }
            />
            <Upload {...uploadProps}>
              <Button>选择 PDF 文件</Button>
            </Upload>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
              <Button onClick={closeUpload}>取消</Button>
              <Button type='primary' loading={uploading} onClick={submitUpload}>上传</Button>
            </div>
          </div>
        </Modal>

        {/* 资料查看弹窗 */}
        <Modal open={docViewer.open} title={`资料 #${docViewer.record?.id || ''}`} onCancel={closeDocViewer} footer={null} width={760}>
          {docViewer.loading ? <div>加载中...</div> : (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {docViewer.list.map(d => {
                let types=[]; try{ types = JSON.parse(d.doc_types||'[]'); if(!Array.isArray(types)) types=[]; }catch(e){ types=[]; }
                const downloadUrl = d.download_url || '';
                return (
                  <Card key={d.id} size='small'>
                    <Space direction='vertical' style={{ width:'100%' }}>
                      <Space style={{ justifyContent:'space-between', width:'100%' }}>
                        <strong>{d.doc_name || d.file_name}</strong>
                        <span style={{ fontSize:12 }}>{(d.file_size/1024).toFixed(1)} KB</span>
                      </Space>
                      <div style={{ fontSize:12, color:'#555' }}>上传时间: {d.created_at}</div>
                      <Space>
                        {types.map(t => <Tag key={t}>{t}</Tag>)}
                      </Space>
                      <Space>
                        <Button size='small' type='primary' onClick={() => setPdfPreview({ open: true, url: downloadUrl, fileName: d.doc_name || d.file_name })} disabled={!downloadUrl}>预览</Button>
                        <Button size='small' type='link' onClick={() => openWithAuth(downloadUrl)} disabled={!downloadUrl}>下载</Button>
                        <Button size='small' danger loading={deletingDocId === d.id} onClick={() => deleteDocument(d)}>删除</Button>
                      </Space>
                    </Space>
                  </Card>
                );
              })}
              {!docViewer.list.length && <Tag color='default'>暂无资料</Tag>}
            </div>
          )}
        </Modal>

        {/* PDF 预览弹窗 */}
        <Modal open={pdfPreview.open} title={pdfPreview.fileName} onCancel={() => setPdfPreview({ open: false, url: '', fileName: '' })} footer={null} width={800}>
          {pdfPreview.url && <SimplePdfViewer url={pdfPreview.url} fileName={pdfPreview.fileName} />}
        </Modal>

        {/* 宿舍分配弹窗 */}
        <Modal open={dormModal.open} title={`宿舍分配 #${dormModal.record?.id || ''}`} onCancel={closeDormModal} footer={null} width={760}>
          {dormModal.loading ? <div>加载中...</div> : (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <Select placeholder='选择宿舍' value={dormModal.selectedDorm} onChange={(v)=> setDormModal(d=>({ ...d, selectedDorm: v, selectedRoom: null, selectedBed: null }))} options={dormModal.dorms.map(d=>({ label: d.name, value: d.id }))} />
              {dormModal.selectedDorm && (
                <Select placeholder='选择房间' value={dormModal.selectedRoom} onChange={(v)=> setDormModal(d=>({ ...d, selectedRoom: v, selectedBed: null }))} options={(dormModal.dorms.find(dd=>dd.id===dormModal.selectedDorm)?.rooms || []).map(r=>({ label: r.room_number + ` (${r.bed_count}床)`, value: r.id }))} />
              )}
              {dormModal.selectedRoom && (
                <Select placeholder='选择床位' value={dormModal.selectedBed} onChange={(v)=> setDormModal(d=>({ ...d, selectedBed: v }))} options={(dormModal.dorms.find(dd=>dd.id===dormModal.selectedDorm)?.rooms || []).find(rr=>rr.id===dormModal.selectedRoom)?.occupants.filter(o=>o.is_empty).map(o=>({ label: `床位 ${o.bed_index}`, value: o.bed_index })) || []} />
              )}
              <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
                <Button onClick={closeDormModal}>取消</Button>
                <Button type='primary' onClick={submitDorm}>确认分配</Button>
              </div>
            </div>
          )}
        </Modal>
    </div>
  );
};

export default EmployeeOnboardingFlowPage;
